/**
 * agentPrepRef.js — which chat message a "Send to your doctor" card belongs to. PROD-1157.
 *
 * ★★★ THE DEFECT. The case-page (inline) chat folds a thread longer than INLINE_FOLD_AFTER
 *   messages down to the last INLINE_TAIL, and handed the card its index in the FOLDED list.
 *   The store looked that index up in the FULL list, found an older message whose prep had
 *   already been sent (null), refused with 'no_prep' and the card said "something went wrong
 *   on our end" — without any request ever leaving the app. Every second prep send in a
 *   folded thread failed this way.
 *
 * THE FIX. The card carries a stable identity of its message: the message's own `id` when it
 * has one (new agent replies get one, see newAgentMessageId), otherwise its index in the FULL
 * list plus its timestamp. The store resolves that identity against the list it holds, which
 * also survives loadCaseMessages re-sorting the list by time between render and send.
 *
 * Pure module, no imports: scripts/check-prep-send-identity.mjs imports it directly, and the
 * inline chat folds through foldThread() so the gate runs the same fold the screen draws.
 */

export const INLINE_FOLD_AFTER = 4
export const INLINE_TAIL = 3

/** The inline chat's fold: what is drawn, and where the drawn part starts in the full list. */
export function foldThread(messages, showEarlier) {
  const list = Array.isArray(messages) ? messages : []
  const folded = !showEarlier && list.length > INLINE_FOLD_AFTER
  const visible = folded ? list.slice(-INLINE_TAIL) : list
  return { folded, visible, offset: list.length - visible.length, earlierCount: list.length - INLINE_TAIL }
}

/** A fresh id for an agent message. Random, carries nothing from the message. */
export function newAgentMessageId() {
  return `am-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/** The identity a prep card sends with: the message id if present, else its FULL-list index. */
export function prepMsgRef(msg, fullIndex) {
  if (msg && typeof msg.id === 'string' && msg.id) return { id: msg.id }
  return { index: fullIndex, timestamp: msg?.timestamp ?? null }
}

/**
 * The index in `messages` of the message `ref` names, or -1. `ref` is what prepMsgRef returned
 * (a bare number is read as a full-list index). By id: that message or nothing — never a
 * neighbour. By index: that slot if its timestamp still matches; if the list moved, the one
 * message carrying a prep with that timestamp.
 */
export function resolvePrepIndex(messages, ref) {
  const list = Array.isArray(messages) ? messages : []
  const r = typeof ref === 'number' ? { index: ref } : (ref || {})
  if (typeof r.id === 'string' && r.id) return list.findIndex((m) => m?.id === r.id)
  if (!Number.isInteger(r.index)) return -1
  const at = list[r.index]
  if (at && (r.timestamp == null || at.timestamp === r.timestamp)) return r.index
  if (r.timestamp == null) return -1
  const hits = []
  list.forEach((m, i) => { if (m?.messengerPrep && m.timestamp === r.timestamp) hits.push(i) })
  return hits.length === 1 ? hits[0] : -1
}
